// ── PRF HISTORICAL EVIDENCE MATRIX — every year, every interval ─────────────
//
// The one PRF visual Kevin uses: 78 years x 11 intervals for a grid, in four
// views over the SAME years and the SAME intervals. Every valid interval is
// present whether or not the farmer elected it, and the elected ones are marked
// — a column the policy does not carry is a counterfactual, not a suggestion.
//
// This file was "prfIntervalAnalysis" and carried a ranking half as well: a
// per-lookback rank, a stability verdict and a per-interval standing. That was
// removed on 2026-09-02 because it was confusing on screen and read as advice,
// which PRF here is deliberately not. What it protected is preserved: the RULES
// below are about not inventing a measurement, and they are what the checker
// still guards.
//
// Pure: no network, no database, no React.

const LAB = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL", "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];

// RMA pays this share of the guarantee when the index lands below the coverage
// level, and nothing at or above it. Kept here because the matrix is the only
// caller left; it is RMA formula, not a model.
function paymentFactor(coverage, index) {
  if (!(coverage > 0) || !Number.isFinite(index)) return null;
  return index >= coverage ? 0 : (coverage - index) / coverage;
}

// ── LEVEL 3: the complete year x interval evidence matrix ─────────────
//
// Four views over the SAME years and the SAME intervals. The simplified pages
// summarise this; they never replace it, and every valid interval is present
// whether or not the farmer elected it.
//
// ⚠ RAINFALL IS SHOWN ONLY WHERE RMA PUBLISHES BOTH HALVES, AND IS NEVER
// RECONSTRUCTED. An index is `actual / normal`, so inches can always be
// back-solved as `index x normal` — and that number would look authoritative
// while being our arithmetic rather than RMA's measurement. Where either half is
// missing the cell says so and stays empty. Measured 2026-09-01:
// `final_rainfall_inches` exists for 2017-2026 only, so every earlier year is a
// legitimate blank in this view and fully populated in the others.
//
// ⚠ AND "% OF NORMAL" IS NOT A SECOND QUANTITY. RMA's own published field is
// `PercentOfNormal` and it IS `IntervalMeasurement / AverageIntervalMeasurement`
// — the index and the percent of normal are definitionally the same number, not
// two measures that happen to agree. They are therefore ONE view, labelled as
// one, rather than two columns inviting a reader to compare them.
const MATRIX_VIEWS = [
  { id: "index", label: "% of Normal (RMA index)",
    note: "RMA's published index. It IS actual divided by normal — the same field, not a separate calculation." },
  { id: "rainfall", label: "Rainfall (in)",
    note: "RMA's own measured inches against RMA's published normal. Blank where RMA publishes no inches for that year — never back-solved from the index." },
  { id: "factor", label: "Payment factor",
    note: "(coverage - index) / coverage, floored at zero. What RMA pays per dollar of protection in that interval." },
  { id: "indemnity", label: "Indemnity $/ac",
    note: "Payment factor x protection per acre, at 100% of value in that interval — so intervals are comparable. Elected intervals carry their real share separately." },
];

function buildEvidenceMatrix({
  series, years, coverage, allocation = {}, protectionPerAcre = null,
  inches = null, normals = null, labels = LAB,
} = {}) {
  if (!series || !Array.isArray(years) || !years.length) {
    return { available: false, reason: "no index history for this grid", views: MATRIX_VIEWS, rows: [], intervals: [] };
  }
  const sorted = [...years].sort((a, b) => b - a);   // newest first — the years a reader checks first

  const ivs = labels.map((l) => ({
    interval: l,
    elected: (Number(allocation[l]) || 0) > 0,
    allocation: Number(allocation[l]) || 0,
    normal_in: normals?.[l] != null && Number.isFinite(Number(normals[l])) ? Number(normals[l]) : null,
  }));

  const rows = sorted.map((year) => ({
    year,
    cells: ivs.map(({ interval, elected, allocation: alloc, normal_in }) => {
      const idx = series[interval]?.[year];
      const hasIdx = Number.isFinite(idx);
      const factor = hasIdx ? paymentFactor(coverage, idx) : null;
      const inch = inches?.[interval]?.[year];
      const hasInch = Number.isFinite(inch);
      return {
        interval, elected, allocation: alloc,
        index: hasIdx ? idx : null,
        factor,
        indemnity_per_acre: factor != null && Number.isFinite(protectionPerAcre)
          ? factor * protectionPerAcre : null,
        // BOTH halves or neither. A lone inches figure with no normal cannot be
        // read, and a normal with no actual is not an observation.
        rainfall_in: hasInch && normal_in != null ? inch : null,
        normal_in: hasInch && normal_in != null ? normal_in : null,
        rainfall_unavailable_reason: hasInch && normal_in != null ? null
          : !hasInch ? "RMA publishes no measured inches for this year"
          : "no published normal for this interval",
      };
    }),
  }));

  const withRain = rows.filter((r) => r.cells.some((c) => c.rainfall_in != null));
  return {
    available: true, views: MATRIX_VIEWS, intervals: ivs, rows,
    rainfall_years: withRain.length,
    rainfall_range: withRain.length
      ? { from: Math.min(...withRain.map((r) => r.year)), to: Math.max(...withRain.map((r) => r.year)) } : null,
    year_range: { from: sorted[sorted.length - 1], to: sorted[0] },
  };
}

module.exports = { LAB, MATRIX_VIEWS, paymentFactor, buildEvidenceMatrix };
